/* Ninja Transfers — Klaviyo custom events module.
 * Spec: klaviyo-events-implementation-spec.md (rev 3 + review findings).
 *
 * Contract: window.ntKlaviyo.track(eventName, properties) — fire-and-forget,
 * never throws. Everything else is private.
 *
 * Pipeline (§4.2), each stage can drop the event:
 *   allowlist → kill switch → consent → schema validation → sensitivity strip
 *   → dedupe check → push → record dedupe key.
 *
 * The pipeline is fully SYNCHRONOUS. isIdentified() resolves once at init into
 * a cached boolean (false until it resolves — a missing property, never a
 * leaked one). An await before push() loses events to immediate navigation:
 * that mistake cost Cart Added v1 ~48% of adds.
 *
 * Kill switch: snippets/nt-events.liquid sets window.ntKlaviyoDisabled before
 * this file runs. Debug: set window.ntKlaviyoDebug = true in the console to
 * surface warns; production default is silent drops.
 *
 * Metric names are PERMANENT in Klaviyo (no rename; delete loses history).
 * Never add a name here that is not in the spec's frozen list, and never fire
 * a name not in this catalogue.
 */
(function () {
  'use strict';

  if (window.ntKlaviyo) return; // double-install guard

  /* ---------------------------------------------------------------- *
   * Debug + closed code sets (§5.4)
   * ---------------------------------------------------------------- */

  function warn() {
    if (window.ntKlaviyoDebug === true) {
      try { console.warn.apply(console, ['[nt-events]'].concat([].slice.call(arguments))); } catch (e) { /* noop */ }
    }
  }

  var FAILURE_CODE_UPLOAD = ['too_large', 'bad_format', 'corrupt', 'dimensions_invalid',
    'timeout', 'network', 'server_error', 'unknown'];
  var FAILURE_CODE_AI = ['quota_exceeded', 'timeout', 'bad_input', 'content_rejected',
    'server_error', 'unknown'];
  var REASON_CODE_HANDOFF = ['customer_requested', 'low_confidence', 'order_specific',
    'complaint', 'refund_request', 'unsupported_topic'];

  var UPLOAD_SOURCES = ['pdp_by_size', 'pdp_uv', 'premium_upload', 'portal_designs',
    'pdp_stickers', 'pdp_large_format', 'pdp_keychains', 'pdp_magnets', 'other_pdp'];
  var AI_TOOLS = ['design_studio', 'image_generator', 'background_remover', 'upscaler', 'mockup_creator'];
  var FILE_TYPES = ['png', 'jpg', 'pdf', 'ai', 'svg', 'other'];
  var DATE_BUCKETS = ['today', '1-3d', '4-7d', '>7d'];

  /* ---------------------------------------------------------------- *
   * Catalogue (§5.1 frozen names + §5.2 schemas). dedupeMs 'page' means
   * once per page lifetime. `send: false` fields key dedupe and are never
   * transmitted. preview_asset_id is deliberately absent until the preview
   * derivative ships (RB10) — adding it later bumps schemaVersion.
   * ---------------------------------------------------------------- */

  var CATALOGUE = {
    'File Uploaded': {
      // v2 (2026-09-03, Justin): image_url / image_urls — the stored imgix URLs
      // of the uploaded files, so flows can show the customer's own artwork.
      // v3 (2026-09-23, Hayes device-context spec): deviceContext adds the
      // module-injected device_type / os / surface / surface_signal.
      schemaVersion: 3, deviceContext: true, dedupe: ['source', 'upload_batch_id'], dedupeMs: 5000,
      props: {
        source: { type: 'enum', required: true, values: UPLOAD_SOURCES },
        upload_batch_id: { type: 'string', send: false },
        product_id: { type: 'string' },
        size: { type: 'string' },
        file_count: { type: 'integer' },
        file_type: { type: 'enum', values: FILE_TYPES },
        dimensions: { type: 'string' },
        dpi: { type: 'integer' },
        art_review_flag: { type: 'boolean' },
        image_url: { type: 'string' },      // first file of the batch
        image_urls: { type: 'string[]' }    // every file of the batch (capped at 25)
      }
    },
    'File Upload Failed': { // no callsites until RB14 names a monitoring destination
      schemaVersion: 1, dedupe: ['source', 'failure_code'], dedupeMs: 30000,
      props: {
        source: { type: 'enum', required: true, values: UPLOAD_SOURCES },
        failure_code: { type: 'enum', required: true, values: FAILURE_CODE_UPLOAD },
        file_type: { type: 'enum', values: FILE_TYPES },
        file_size_bucket: { type: 'enum', values: ['<5mb', '5-25mb', '25-100mb', '>100mb'] }
      }
    },
    'Reorder Intent': {
      schemaVersion: 1, dedupe: ['source', 'design_id', 'order_id'], dedupeMs: 30000,
      props: {
        source: { type: 'enum', required: true, values: ['pdp_previous_designs', 'portal_dashboard', 'portal_order_row'] },
        design_id: { type: 'string' },
        order_id: { type: 'string' },
        order_total: { type: 'number' },
        currency: { type: 'string' },
        days_since_order: { type: 'integer' }
      }
    },
    'Key Page Viewed': {
      // v2 (2026-09-23, Hayes device-context spec): deviceContext.
      // v3 (2026-10-06, Hayes key-page-toggle spec): + section 'custom_landing_page', sent only for
      //   pages toggled on in admin (page metafield custom.key_page_tracking). Readbacks: schema_version >= 2.
      schemaVersion: 3, deviceContext: true, dedupe: ['path'], dedupeMs: 'page',
      props: {
        path: { type: 'string', required: true },
        section: { type: 'enum', required: true, values: ['tools', 'rewards', 'account', 'b2b', 'support', 'education', 'trust', 'shop', 'custom_landing_page'] }
      }
    },

    /* Wave 1B — schemas frozen now, callsites ship after the 1A 30-day review. */
    'Cross Brand Click': {
      schemaVersion: 1, dedupe: ['destination_key', 'source'], dedupeMs: 10000,
      props: {
        destination_key: { type: 'enum', required: true, values: ['patches', 'printhouse', 'pod', 'dtftransfers', 'blanks'] },
        source: { type: 'enum', required: true, values: ['top_strip', 'mega_menu', 'footer'] }
      }
    },
    'Portal Quick Action Clicked': {
      schemaVersion: 1, dedupe: ['action'], dedupeMs: 10000,
      props: {
        action: { type: 'enum', required: true, values: ['start_new_order', 'track_order', 'reorder', 'fix_a_problem', 'edit_with_ai'] }
      }
    },
    'Shinobi Handoff to Human': {
      schemaVersion: 1, dedupe: ['reason_code'], dedupeMs: 30000,
      props: {
        reason_code: { type: 'enum', required: true, values: REASON_CODE_HANDOFF },
        estimated_wait_bucket: { type: 'enum', values: ['<1m', '1-5m', '5-15m', '>15m'] }
      }
    },
    'In-App Message CTA Clicked': {
      schemaVersion: 1, dedupe: ['message_id', 'cta_key'], dedupeMs: 30000,
      props: {
        message_id: { type: 'string', required: true },
        cta_key: { type: 'string', required: true },
        destination_key: { type: 'string' }
      }
    },
    'AI Tool Started': {
      schemaVersion: 1, dedupe: ['tool'], dedupeMs: 30000,
      props: {
        tool: { type: 'enum', required: true, values: AI_TOOLS },
        entry_point: { type: 'enum', values: ['tools_hub', 'pdp', 'portal', 'direct'] }
      }
    },
    'Account Viewed': { // 1B; fired by the §6.15 pane observer (not armed in 1A)
      schemaVersion: 1, dedupe: [], dedupeMs: 0, // real-transition test in the observer replaces windows
      props: {
        section: { type: 'enum', required: true, values: ['dashboard', 'orders', 'designs', 'design_library', 'messages', 'avatar', 'rewards', 'profile'] },
        item: { type: 'string' },
        item_type: { type: 'enum', values: ['order', 'design', 'category', 'message'] }
      }
    }
  };
  Object.keys(CATALOGUE).forEach(function (k) {
    Object.freeze(CATALOGUE[k].props);
    Object.freeze(CATALOGUE[k]);
  });
  Object.freeze(CATALOGUE);

  /* ---------------------------------------------------------------- *
   * Device context (klaviyo-events-device-context-spec.md §5), injected
   * by track() for entries flagged deviceContext. NOT in any props
   * schema: validation iterates caller-supplied keys, so a props entry
   * would either drop every event (required) or do nothing (optional).
   * device_type / os are stable for the page → computed once here.
   * surface is re-derived on every event and never cached: this file is
   * deferred, so it evaluates BEFORE DOMContentLoaded, and Apploy builds
   * window.apployAppManager ON DOMContentLoaded. A cached answer would
   * freeze a premature "web", which is byte-identical to a real one.
   * The raw user-agent string is never sent (§7.1).
   * ---------------------------------------------------------------- */
  var NT_DEVICE = (function () {
    try {
      var ua  = navigator.userAgent || '';
      var uad = navigator.userAgentData;                 // Chromium only; undefined elsewhere
      // iPadOS 13+ reports itself as a Mac. maxTouchPoints is the only reliable tell.
      var isIpad = /iPad/.test(ua) ||
                   (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      // Android tablets report userAgentData.mobile === false AND omit the "Mobile"
      // UA token. Both paths must use the same rule or Chromium says "desktop" where
      // Firefox says "mobile" for the same device.
      var isMobile = uad ? uad.mobile === true
                         : (/iPhone|iPod|Windows Phone/i.test(ua) ||
                            (/Android/i.test(ua) && /Mobile/i.test(ua)));
      var os = 'other';
      if (isIpad || /iPhone|iPod/.test(ua))    os = 'ios';
      else if (/Android/i.test(ua))            os = 'android';
      else if (/Windows/i.test(ua))            os = 'windows';
      else if (/Mac OS X|Macintosh/i.test(ua)) os = 'macos';
      var isAndroidTablet = (os === 'android' && !isMobile);
      return {
        device_type: (isIpad || isAndroidTablet) ? 'tablet' : (isMobile ? 'mobile' : 'desktop'),
        os: os
      };
    } catch (e) { return null; }
  })();

  function resolveSurface() {
    try {
      var mgr    = window.apployAppManager;
      var ready  = !!(mgr && typeof mgr.isMobileApp === 'function');
      var bridge = (typeof window.ReactNativeWebView !== 'undefined');

      if (ready) {
        if (mgr.isMobileApp() === true)
          return { surface: 'app', surface_signal: 'app_manager' };
        if (bridge)                                  // manager says no, bridge says yes
          return { surface: 'app', surface_signal: 'rn_webview_override' };
        return { surface: 'web', surface_signal: 'app_manager' };
      }
      if (bridge)                                    // pre-DCL, inside the app
        return { surface: 'app', surface_signal: 'rn_webview' };

      // Neither signal available yet. On a browser this means web, but it is
      // indistinguishable from "too early to tell", so it is not asserted.
      return { surface: null, surface_signal: 'unresolved' };
    } catch (e) {
      return { surface: null, surface_signal: 'detector_error' };
    }
  }

  /* ---------------------------------------------------------------- *
   * Consent (§4.3) — gate on Shopify's Customer Privacy API, fail closed.
   * Read once on init AND subscribe: updates publish only on change.
   * ---------------------------------------------------------------- */

  var consentKnown = false;
  var consentAnalytics = false;
  var consentMarketing = false;

  function readConsent() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp || typeof cp.analyticsProcessingAllowed !== 'function') return;
      consentAnalytics = cp.analyticsProcessingAllowed() === true;
      consentMarketing = cp.marketingAllowed() === true;
      consentKnown = true;
      // Page-load events can only fire once consent is known — a page-lifetime
      // dedupe key makes re-attempts here harmless.
      firePageLoadEvents();
    } catch (e) {
      consentKnown = false;
    }
  }

  function initConsent() {
    try {
      if (!(window.Shopify && typeof window.Shopify.loadFeatures === 'function')) return;
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (err) {
        if (err) { consentKnown = false; return; }
        readConsent();
      });
    } catch (e) { consentKnown = false; }
  }

  // NOTE: consent init runs from the boot block at the END of this IIFE —
  // readConsent() fires page-load events, which need every declaration
  // below (KEY_PAGES included) to already be assigned.

  /* ---------------------------------------------------------------- *
   * Identity cache (§4.4) — resolve once, own catch, default false.
   * The send path only ever reads the boolean.
   * ---------------------------------------------------------------- */

  var ntIdentified = false;
  var identityRequested = false;

  function attemptIdentityResolve() {
    if (identityRequested) return;
    try {
      if (window.klaviyo && typeof window.klaviyo.isIdentified === 'function') {
        identityRequested = true;
        window.klaviyo.isIdentified()
          .then(function (v) { ntIdentified = v === true; })
          .catch(function () { ntIdentified = false; });
      }
    } catch (e) { ntIdentified = false; }
  }
  // attemptIdentityResolve() runs from the boot block at the end.

  /* ---------------------------------------------------------------- *
   * Validation, dedupe, send (§4.2)
   * ---------------------------------------------------------------- */

  var dedupeMap = new Map(); // page-lifetime only (§4.5)

  function validType(def, val) {
    switch (def.type) {
      case 'string': return typeof val === 'string' && val !== '';
      case 'integer': return typeof val === 'number' && isFinite(val) && Math.floor(val) === val;
      case 'number': return typeof val === 'number' && isFinite(val);
      case 'boolean': return typeof val === 'boolean';
      case 'enum': return typeof val === 'string' && def.values.indexOf(val) !== -1;
      case 'string[]': return Array.isArray(val) && val.length > 0 && val.every(function (x) { return typeof x === 'string' && x !== ''; });
      default: return false;
    }
  }

  function track(name, props) {
    try {
      // 1. allowlist — the only thing between a typo and a permanent metric
      if (!Object.prototype.hasOwnProperty.call(CATALOGUE, name)) {
        warn('unknown event name dropped:', name);
        return;
      }
      // 2. kill switch
      if (window.ntKlaviyoDisabled === true) return;
      // 3. consent — both analytics AND marketing; fail closed
      if (!(consentKnown && consentAnalytics && consentMarketing)) return;

      var schema = CATALOGUE[name];
      props = props || {};

      // 4. schema validation. Required failure drops the EVENT; an invalid
      //    optional value drops the PROPERTY (rejected, never coerced).
      var accepted = {}; // includes send:false fields (for dedupe)
      var keys = Object.keys(schema.props);
      for (var i = 0; i < keys.length; i++) {
        var key = keys[i];
        var def = schema.props[key];
        var val = props[key];
        var absent = val === undefined || val === null || val === '';
        if (absent) {
          if (def.required) { warn(name, 'missing required', key, '— event dropped'); return; }
          continue; // omit absent values entirely (§7.4)
        }
        if (!validType(def, val)) {
          if (def.required) { warn(name, 'invalid required', key, '— event dropped'); return; }
          warn(name, 'invalid optional', key, '— property dropped');
          continue;
        }
        accepted[key] = val;
      }
      // money rule (§7.8): currency required whenever a monetary value is present
      if ((accepted.order_total !== undefined || accepted.estimated_price !== undefined) &&
          accepted.currency === undefined) {
        warn(name, 'monetary value without currency — event dropped');
        return;
      }

      // 5. sensitivity strip (§7.2) — strips properties, never drops the event
      var payload = {};
      for (var j = 0; j < keys.length; j++) {
        var k2 = keys[j];
        if (accepted[k2] === undefined) continue;
        if (schema.props[k2].send === false) continue;             // dedupe-only
        if (schema.props[k2].identifiedOnly && !ntIdentified) continue;
        payload[k2] = accepted[k2];
      }
      payload.schema_version = schema.schemaVersion; // §5.3 — module-injected
      // Device context (device-context spec §6). After validation, before
      // dedupe: the dedupe key is built from `accepted`, so it never sees these.
      // The two failures are independent and neither drops the event.
      if (schema.deviceContext) {
        if (NT_DEVICE) {                            // device failure omits device only
          payload.device_type = NT_DEVICE.device_type;
          payload.os          = NT_DEVICE.os;
        }
        var surfaceCtx = resolveSurface();          // surface failure omits surface only
        if (surfaceCtx.surface) payload.surface = surfaceCtx.surface;
        payload.surface_signal = surfaceCtx.surface_signal;
      }

      // 6. dedupe check (do not record yet)
      var dKey = '';
      if (schema.dedupe.length) {
        var parts = [name];
        for (var d = 0; d < schema.dedupe.length; d++) {
          var dv = accepted[schema.dedupe[d]];
          parts.push(dv === undefined ? '' : String(dv));
        }
        dKey = parts.join('|');
        var winMs = schema.dedupeMs === 'page' ? Infinity : schema.dedupeMs;
        var seen = dedupeMap.get(dKey);
        if (seen !== undefined && (Date.now() - seen) < winMs) return;
      }

      // 7. send synchronously, then record the key only on a clean push
      window.klaviyo.push(['track', name, payload]);
      if (dKey) dedupeMap.set(dKey, Date.now());
    } catch (e) {
      warn('track() error:', e && e.message);
    }
  }

  window.ntKlaviyo = Object.freeze({ track: track });

  /* ---------------------------------------------------------------- *
   * Debounce helper (§4.5) — trailing, per key
   * ---------------------------------------------------------------- */

  var debounceTimers = {};
  function debounced(key, ms, fn) {
    if (debounceTimers[key]) clearTimeout(debounceTimers[key]);
    debounceTimers[key] = setTimeout(function () {
      delete debounceTimers[key];
      try { fn(); } catch (e) { warn('debounced fn error:', e && e.message); }
    }, ms);
  }

  /* Delivery Estimate Checked, Price Calculator Used, AI Tool Completed/Failed and
     Design Library Item Selected were removed from the allowlist 2026-09-03 (Justin,
     after Hayes' test: 'acting peculiar and not worth it or not working'). track()
     drops any name not in the catalogue, whatever calls it. */

  /* ---------------------------------------------------------------- *
   * Callsite: Key Page Viewed (§6.9)
   * BLOCKED ON RB6 — the path→section table below ships EMPTY until the
   * final list is signed off by marketing. With an empty table this never
   * fires. Populate as '/pages/x': 'section' (canonical path, no query).
   * ---------------------------------------------------------------- */

  // RB6 closed 2026-09-01: 19 paths supplied by the owner, all verified 200,
  // plus /pages/my-design added 2026-09-02 ahead of that page shipping.
  // Flagged-but-wired rows (spec §6.9): /pages/welcome will double-report
  // against Account Viewed once 1B arms the pane observer, and the footer
  // links /policies/refund-policy while this list tracks /pages/refund-policy.
  var KEY_PAGES = Object.freeze({
    '/pages/free-design-tools': 'tools',
    '/pages/ninja-rewards': 'rewards',
    '/pages/welcome': 'account',
    // NOT YET LIVE (404 today) — deliberate: costs no metric budget and never
    // fires until the page ships. ON SHIP DAY: (1) confirm the final path —
    // the live PLURAL /pages/my-designs is a different, existing portal page,
    // and a path drift here reads as low traffic instead of broken; (2) if the
    // new page renders the portal shell (mp-panel markup), it will also trip
    // the 1B Account Viewed observer — assert the exact event count in the §9
    // per-surface test so a double-fire is caught, not absorbed.
    '/pages/my-design': 'account',
    '/pages/influencer': 'b2b',
    '/pages/tax-exemption-form': 'b2b',
    '/pages/order-issue': 'support',
    '/pages/returnsensei': 'support',
    '/a/faq': 'support',
    '/pages/apply': 'education',
    '/pages/ninja-university': 'education',
    '/pages/dtf-application-pressing': 'education',
    '/pages/heat-press-instructions-for-dtf-transfers': 'education',
    '/pages/blog': 'education',
    '/pages/about': 'trust',
    '/pages/ninja-transfers-reviews': 'trust',
    '/pages/satisfaction-guarantee': 'trust',
    '/policies/shipping-policy': 'trust',
    '/pages/refund-policy': 'trust',
    '/pages/dtf-transfers-uv-dtf-3d-permastickers': 'shop'
  });

  // Runs from readConsent() once consent is known (never at module eval —
  // firing before the Customer Privacy API resolves would drop the event
  // with no retry). The 'page' dedupe window absorbs repeat calls.
  // Per-page toggle (klaviyo-key-page-toggle-spec.md, 2026-10-06): a /pages/* page toggled on in
  // admin renders <meta name="nt-key-page"> (snippets/nt-key-page-meta.liquid) and fires with
  // section 'custom_landing_page'. The hard-coded list above still wins on any conflict. Do NOT put
  // 'custom_landing_page' on a KEY_PAGES entry — the value means exactly "toggled on in admin".
  function firePageLoadEvents() {
    try {
      var path = window.location.pathname.replace(/\/+$/, '') || '/';
      var section = KEY_PAGES[path];                      // hard-coded list wins
      if (!section && document.querySelector('meta[name="nt-key-page"]')) {
        section = 'custom_landing_page';
      }
      if (!section) return;
      track('Key Page Viewed', { path: path, section: section });
    } catch (e) { warn('key page error:', e && e.message); }
  }

  /* ---------------------------------------------------------------- *
   * Callsite: File Uploaded (§6.1) — upload surfaces
   *
   * The upload engines announce completion; ALL Klaviyo knowledge (source
   * classification, schema, batch id) stays here. Two signals:
   *
   *  - `nt:file-uploaded` on document, detail {count, ext} — dispatched by
   *    the legacy single-file pipeline in pdp_transferBySize.js after the
   *    S3 PUT (stickers / large format / keychains / magnets / gang-sheet
   *    step uploads). The AI-edit re-upload path deliberately does not
   *    dispatch: an edit is not a customer upload.
   *
   *  - The v4 engine's own `bsv4:*` announces (by-size-v4 / uv-by-size-v4
   *    templates), correlated below. `bsv4:files-received` WITHOUT a
   *    source field is a fresh drop; `source: 'previous-designs'` is the
   *    Use-Design/handoff path landing existing art — never counted.
   * ---------------------------------------------------------------- */

  var EXT_TO_TYPE = { png: 'png', jpg: 'jpg', jpeg: 'jpg', pdf: 'pdf', ai: 'ai', svg: 'svg' };

  function uploadSource() {
    try {
      var cls = document.body.className || '';
      if (cls.indexOf('nt-gs-sheet') !== -1) return 'premium_upload';
      if (cls.indexOf('template-product-by-size-v4') !== -1) return 'pdp_by_size';
      if (cls.indexOf('template-product-uv-by-size-v4') !== -1) return 'pdp_uv';
      var type = '';
      try {
        var mp = window.meta && window.meta.product;
        if (mp && typeof mp.type === 'string') type = mp.type.toLowerCase().trim();
      } catch (e) { /* meta absent */ }
      if (type === 'stickers' || type === 'truck stickers' || type === 'bumper stickers') return 'pdp_stickers';
      if (type === 'large stickers') return 'pdp_large_format';
      if (type === 'keychains') return 'pdp_keychains';
      if (type === 'magnets') return 'pdp_magnets';
      if (cls.indexOf('template-product-by-size-bg-options') !== -1) return 'pdp_by_size';
      if (cls.indexOf('template-product-uv-dtf-by-size') !== -1) return 'pdp_uv';
      return 'other_pdp';
    } catch (e) { return 'other_pdp'; }
  }

  function cleanUrls(urls) {
    var out = [], i, u;
    if (!urls) return out;
    for (i = 0; i < urls.length && out.length < 25; i++) {
      u = urls[i];
      if (typeof u !== 'string') continue;
      u = u.split('?')[0];
      /* MEDIUM tier (imgix audit 2026-09-11): never hand an email the bare
         master (a full-size passthrough render on every open); send the shared
         w=600 derivative every other medium surface uses. */
      if (/\.(ai|eps|pdf|svg)$/i.test(u)) u += '?fm=png&auto=compress&q=50&w=600'; else u += '?trim=colorUnlessAlpha&fm=png&auto=compress&q=50&w=600';
      if (/^https?:\/\//.test(u)) out.push(u);
    }
    return out;
  }

  function trackFileUploaded(count, ext, urls) {
    var props = {
      source: uploadSource(),
      upload_batch_id: 'nt-' + Date.now() + '-' + Math.floor(Math.random() * 1000000)
    };
    if (typeof count === 'number' && count > 0) props.file_count = count;
    if (ext) props.file_type = EXT_TO_TYPE[String(ext).toLowerCase()] || 'other';
    var clean = cleanUrls(urls);
    if (clean.length) { props.image_url = clean[0]; props.image_urls = clean; }
    try {
      var mp = window.meta && window.meta.product;
      if (mp && mp.id) props.product_id = String(mp.id);
    } catch (e) { /* not a PDP */ }
    track('File Uploaded', props);
  }

  document.addEventListener('nt:file-uploaded', function (e) {
    try {
      var d = (e && e.detail) || {};
      trackFileUploaded(typeof d.count === 'number' ? d.count : 1, d.ext, d.urls || (d.url ? [d.url] : null));
    } catch (err) { warn('nt:file-uploaded listener error:', err && err.message); }
  });

  // v4 correlation: batches can overlap, so counters accumulate between
  // upload-complete settlements. A failed file decrements; if nothing fresh
  // actually landed, no event.
  var bsv4Fresh = 0;
  var bsv4Fails = 0;
  document.addEventListener('bsv4:files-received', function (e) {
    try {
      var d = (e && e.detail) || {};
      if (d.source === 'previous-designs') return; // re-used art, not an upload
      if (typeof d.count === 'number' && d.count > 0) bsv4Fresh += d.count;
    } catch (err) { /* noop */ }
  });
  // the engine's own store holds each uploaded file's imgix URL; the last
  // `n` entries are the batch that just settled
  function bsv4FileUrls(n) {
    try {
      var st = window.__bsv4 && typeof window.__bsv4.getState === 'function' ? window.__bsv4.getState() : null;
      var files = st && st.files ? st.files : [];
      var out = [], i, f;
      for (i = Math.max(0, files.length - n); i < files.length; i++) {
        f = files[i];
        if (f && (f.imgixUrl || f.s3Url)) out.push(String(f.imgixUrl || f.s3Url));
      }
      return out;
    } catch (e) { return []; }
  }
  document.addEventListener('bsv4:file-failed', function () {
    bsv4Fails++;
  });
  document.addEventListener('bsv4:upload-complete', function () {
    try {
      var landed = bsv4Fresh - bsv4Fails;
      bsv4Fresh = 0;
      bsv4Fails = 0;
      if (landed > 0) trackFileUploaded(landed, null, bsv4FileUrls(landed));
    } catch (err) { warn('bsv4 listener error:', err && err.message); }
  });

  /* ---------------------------------------------------------------- *
   * Boot — LAST, so every declaration above is assigned before anything
   * can fire (deferred scripts run at readyState 'interactive', so an
   * early initConsent() would race var assignments).
   * ---------------------------------------------------------------- */

  document.addEventListener('visitorConsentCollected', function () {
    readConsent();
    attemptIdentityResolve(); // consent may have just let klaviyo.js load
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initConsent);
  } else {
    initConsent();
  }
  attemptIdentityResolve();
})();
